<script setup lang="ts">
    import { useShippingStore } from '@/stores/shippingStore';
    import { isPackageEditable, packageMetadata } from './shippingPackageMetadata';
    import { longDate } from '@/formatters';
    import { ShippingPackage } from '@/shared/helpers/api';
    import useShow from '@/shared/composables/useShow';

    const store = useShippingStore();
    const { showError } = useShow();

    async function onEdit(shippingPackage: ShippingPackage): Promise<void> {
        try {
            await store.startEditWizard(shippingPackage);
        } catch (err) {
            showError(err);
        }
    }
</script>

<template>
    <b-table
        v-show="store.showPackageList"
        striped
        paginated
        hoverable
        per-page="25"
        :data="store.packages"
        @click="store.setSelectedPackage">
        <b-table-column
            v-slot="props"
            field="name"
            cell-class="is-clickable"
            label="Name">
            {{ props.row.name || props.row.packageId }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="description"
            cell-class="is-clickable"
            label="Description">
            {{ props.row.description }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="packageType"
            cell-class="is-clickable"
            label="Package type">
            <b-icon
                :icon="packageMetadata.type.icon(props.row.packageType)"
                :custom-class="packageMetadata.type.class(props.row.packageType)"
                size="is-small" />
            {{ packageMetadata.type.displayAs(props.row.packageType) }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="dateExtracted"
            cell-class="is-clickable"
            label="Extracted (UTC)">
            {{ longDate(props.row.dateExtracted) }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="extractedFromSystem"
            cell-class="is-clickable"
            label="From system">
            {{ props.row.extractedFromSystem }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="status"
            cell-class="is-clickable"
            label="Status">
            <b-icon
                :icon="packageMetadata.status.icon(props.row.status)"
                :custom-class="packageMetadata.status.class(props.row.status)"
                size="is-small" />
            {{ packageMetadata.status.displayAs(props.row.status) }}
        </b-table-column>
        <b-table-column
            v-slot="props"
            field="actions"
            label=""
            cell-class="has-text-right">
            <b-button
                v-if="isPackageEditable(props.row)"
                size="is-small"
                icon-left="pencil"
                title="Edit package setup"
                :disabled="store.startingEdit"
                @click.stop="onEdit(props.row)">
                Edit
            </b-button>
        </b-table-column>
    </b-table>
</template>
